/**
 * FIBES: Loop widget tweaks for non-product pages.
 *
 * Two responsibilities:
 *   1. Continuously set `window.loopPropsUI.byDefaultChooseSubscriptionOption = true`
 *      so Loop's own logic defaults to subscription on initial render and
 *      after variant changes.
 *   2. Safe, passive DOM tweaks that fill gaps in Loop's standard widget:
 *      - Replace Single variant's dropdown with "Delivered: Monthly" text
 *      - Inject per-unit "$X.XX each" price into the price-quantity slot
 *      - Populate subscription -original price from product JSON and show
 *        strikethrough when discounted
 *
 * CRITICAL: This script NEVER clicks radios, fires synthetic clicks, or
 * triggers any Loop event handlers. The crash we saw earlier was caused
 * by programmatic clicks firing Loop handlers that read loopPropsUI
 * before it was populated. We avoid that entire failure mode by being
 * read-only on Loop's event surface — we only mutate display/text.
 */

(function () {
  'use strict';

  // ---------- Flag setter (Loop's own default-to-subscription) ----------
  //
  // Intercept any assignment to window.loopPropsUI via Object.defineProperty.
  // When Loop's CDN fetch completes and tries to set loopPropsUI, our setter
  // catches it, stores the value, and overlays our flag. Loop can NEVER read
  // loopPropsUI.byDefaultChooseSubscriptionOption without seeing `true` —
  // there's no timing window for setInterval to miss.

  let _loopPropsUI =
    (window.loopPropsUI && typeof window.loopPropsUI === 'object')
      ? window.loopPropsUI
      : {};
  _loopPropsUI.byDefaultChooseSubscriptionOption = true;

  try {
    Object.defineProperty(window, 'loopPropsUI', {
      configurable: true,
      enumerable: true,
      get: function () {
        return _loopPropsUI;
      },
      set: function (value) {
        _loopPropsUI = (value && typeof value === 'object') ? value : {};
        _loopPropsUI.byDefaultChooseSubscriptionOption = true;
      },
    });
  } catch (e) {
    // Fallback if defineProperty fails (shouldn't, but safety)
    window.loopPropsUI = _loopPropsUI;
    setInterval(function () {
      if (window.loopPropsUI) {
        window.loopPropsUI.byDefaultChooseSubscriptionOption = true;
      }
    }, 100);
  }

  // ---------- Helpers ----------

  const QTY_MAP = {
    single: 1, one: 1,
    two: 2, double: 2,
    three: 3, triple: 3,
    four: 4, quad: 4, quadruple: 4,
    five: 5,
  };

  function parseQty(name) {
    if (!name) return null;
    const lower = String(name).toLowerCase();
    for (const k in QTY_MAP) {
      if (lower.includes(k)) return QTY_MAP[k];
    }
    const m = lower.match(/(\d+)\s*(pouch|pack|x)/);
    if (m) return parseInt(m[1], 10);
    return null;
  }

  function moneyFormat(amount) {
    return '$' + amount.toFixed(2);
  }

  function extractFrequency(text) {
    const t = (text || '').toLowerCase().trim();
    if (!t) return 'Monthly';
    if (t.includes('monthly') || t.includes('every month') || t.includes('month')) return 'Monthly';
    if (t.includes('biweekly') || t.includes('every 2 week') || t.includes('every two week')) return 'Biweekly';
    if (t.includes('weekly') || t.includes('every week') || t.includes('week')) return 'Weekly';
    if (t.includes('bimonth') || t.includes('every 2 month')) return 'Bimonthly';
    if (t.includes('quarter')) return 'Quarterly';
    const words = t.split(/\s+/);
    const last = words[words.length - 1] || 'monthly';
    return last.charAt(0).toUpperCase() + last.slice(1);
  }

  let variantPriceMap = null;
  function loadVariantPrices() {
    if (variantPriceMap && Object.keys(variantPriceMap).length) return variantPriceMap;
    const script = document.querySelector('script[class^="loopProductQuickJson-"]');
    if (!script) return null;
    try {
      const product = JSON.parse(script.textContent);
      if (!product || !Array.isArray(product.variants) || !product.variants.length) return null;
      const map = {};
      product.variants.forEach((v) => { map[v.id] = v.price; });
      variantPriceMap = map;
      return variantPriceMap;
    } catch (e) {
      return null;
    }
  }

  function findActiveSubscriptionRadio() {
    const inp = document.querySelector(
      'product-form-component input[name="id"], input[name="id"][ref="variantId"]'
    );
    if (inp) {
      const r = document.querySelector(
        '.loop-subscription-group-radio[data-variant-id="' + inp.value + '"]'
      );
      if (r) return r;
    }
    return document.querySelector('.loop-subscription-group-radio');
  }

  // ---------- 1. Single variant: replace dropdown with "Delivered: Monthly" ----------

  function replaceSingleDropdownWithText() {
    document.querySelectorAll('.loop-subscription-group').forEach((group) => {
      const radio = group.querySelector('.loop-subscription-group-radio');
      if (!radio) return;
      const variantName = (radio.dataset.name || '').toLowerCase();
      const qty = parseQty(variantName);
      const isSingleType = qty === 1 || variantName.includes('single');

      const container = group.querySelector('.loop-subscription-group-selling-plans-container');
      if (!container) return;

      const label = container.querySelector('.loop-selling-plan-selector-label');
      const select = container.querySelector('.loop-selling-plan-selector');
      let textEl = container.querySelector('.fibes-loop-delivered-text');

      if (isSingleType) {
        if (label) label.classList.add('fibes-hidden');
        if (select) select.classList.add('fibes-hidden');
        if (!textEl) {
          textEl = document.createElement('div');
          textEl.className = 'fibes-loop-delivered-text';
          container.insertBefore(textEl, container.firstChild);
        }
        let freq = 'Monthly';
        if (select) {
          const opt = select.selectedOptions[0] || select.options[0];
          if (opt && opt.text) freq = extractFrequency(opt.text);
        }
        const desired = 'Delivered: ' + freq;
        if (textEl.textContent !== desired) textEl.textContent = desired;
      } else {
        if (label) label.classList.remove('fibes-hidden');
        if (select) select.classList.remove('fibes-hidden');
        if (textEl) textEl.remove();
      }
    });
  }

  // ---------- 2. Per-unit "$X.XX each" price ----------

  function setPerUnit(priceEl, eachEl, qty) {
    if (!priceEl || !eachEl) return;
    const priceNum = parseFloat((priceEl.textContent || '').replace(/[^\d.]/g, ''));
    if (isNaN(priceNum) || priceNum <= 0) return;
    const perUnit = priceNum / qty;
    eachEl.classList.remove('loop-display-none', 'loop-hidden');
    const desired = moneyFormat(perUnit) + ' each';
    if (eachEl.textContent !== desired) eachEl.textContent = desired;
  }

  function injectPerUnitPrices() {
    document.querySelectorAll('.loop-subscription-group').forEach((group) => {
      const radio = group.querySelector('.loop-subscription-group-radio');
      if (!radio) return;
      const qty = parseQty(radio.dataset.name);
      if (!qty) return;
      setPerUnit(
        group.querySelector('.loop-subscription-group-price-amount'),
        group.querySelector('.loop-subscription-group-price-quantity'),
        qty
      );
    });

    const activeRadio = findActiveSubscriptionRadio();
    const oneTimeQty = activeRadio ? parseQty(activeRadio.dataset.name) : null;
    if (oneTimeQty) {
      document.querySelectorAll('.loop-one-time-purchase-option').forEach((option) => {
        setPerUnit(
          option.querySelector('.loop-one-time-purchase-option-price-amount'),
          option.querySelector('.loop-one-time-purchase-option-price-quantity'),
          oneTimeQty
        );
      });
    }
  }

  // ---------- 3. Strikethrough original price on subscription ----------

  function showStrikethroughIfDiscounted() {
    const priceMap = loadVariantPrices();

    document.querySelectorAll('.loop-subscription-group').forEach((group) => {
      const radio = group.querySelector('.loop-subscription-group-radio');
      const original = group.querySelector('.loop-subscription-group-price-amount-original');
      const current = group.querySelector('.loop-subscription-group-price-amount');
      if (!radio || !original || !current) return;

      // Loop's snippet sets all -original prices to the SELECTED variant's
      // price (which is wrong for non-selected variants). Overwrite with
      // each variant's actual price from the product JSON.
      if (priceMap) {
        const variantId = parseInt(radio.dataset.variantId, 10);
        if (variantId && priceMap[variantId] != null) {
          const cents = priceMap[variantId];
          const formatted = '$' + (cents / 100).toFixed(2);
          if (original.textContent.trim() !== formatted) {
            original.textContent = formatted;
          }
        }
      }

      const origNum = parseFloat((original.textContent || '').replace(/[^\d.]/g, ''));
      const currNum = parseFloat((current.textContent || '').replace(/[^\d.]/g, ''));
      if (isNaN(origNum) || isNaN(currNum)) return;

      if (origNum > currNum) {
        original.classList.remove('loop-display-none', 'loop-hidden');
        original.classList.add('fibes-force-show');
        original.style.display = '';
        original.removeAttribute('hidden');
      } else {
        original.classList.remove('fibes-force-show');
      }
    });
  }

  // ---------- Master tweak fn ----------

  function applyTweaks() {
    replaceSingleDropdownWithText();
    injectPerUnitPrices();
    // showStrikethroughIfDiscounted();
    // ^ Disabled 2026-06-05. Loop's "original" price element pulls variant.price, not
    // compare_at_price, so the strike was misleading. Value strip now owns the strike
    // (compare_at-based). Loop's element defaults to .loop-display-none so it stays hidden.
  }

  // ---------- Bootstrap ----------

  function bootstrap() {
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      const widget = document.querySelector('.loop-subscription-container');
      const radio = widget && widget.querySelector('.loop-subscription-group-radio');
      if (widget && radio) {
        clearInterval(interval);
        applyTweaks();
        // Re-apply on DOM mutations (variant changes, price updates, etc.)
        let debounce;
        const obs = new MutationObserver(() => {
          window.clearTimeout(debounce);
          debounce = window.setTimeout(applyTweaks, 60);
        });
        obs.observe(widget, { childList: true, subtree: true, attributes: true });
      } else if (attempts > 150) {
        clearInterval(interval);
      }
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();